import type { Manifest } from '../api/authoring'
import { fieldsSchema, typeOf } from './manifest'
import { asSchema, isDrawable, namesTheTree, orderedProperties, type Schema } from './schema'

// What a create asks an author for.
//
// **This is the one write on the surface shaped by the *fields* schema
// rather than by an intent's payload**, and it is worth saying why the
// exception is not a crack in the rule. Everywhere else, the payload
// schemas are the only thing that changes anything, because an intent is
// the unit of a revision and "the author changed some fields" is not an
// intent anybody meant. A create is not a change: it is the first
// revision, `component.created`, which the command endpoint refuses
// precisely because it states an id and a tenant that `POST …/components`
// takes from its own path. So there is no payload to derive a form from,
// and the type's own fields are what the request carries.
//
// Which leaves the question this file answers: the fields schema
// describes everything a document of that type *holds*, and a create must
// not ask for all of it. A Media holds a blob key, a content type, pixel
// dimensions and a list of renditions, every one of them a reading the
// finalise step takes off an object that does not exist yet.

/**
 * The properties a create offers, as a schema `SchemaForm` can draw.
 *
 * Three admissions, and their union is the whole rule:
 *
 * - **What the type cannot do without** — a property the schema marks
 *   required, which is a Go field with no `omitempty`. Media's `kind` is
 *   the case that matters: no intent carries it, because a Media never
 *   moves between kinds, so creation is the only moment it can be stated
 *   at all.
 * - **What some declared intent could set anyway** — offering it here
 *   saves an author creating an empty thing and immediately editing it,
 *   and it can hardly be forbidden at creation when the manifest says it
 *   may be sent a second later.
 * - **What the type says is fixed once stated** — a field whose intent
 *   the type withholds *because* it may not change, which is the one
 *   case the second admission gets wrong on its own. A Content's format
 *   is the author's choice and only the changing of it is not, so
 *   withholding `content.format_changed` had quietly taken the choice
 *   away from creation too. `fixed` is the type saying where it is
 *   stated instead.
 *
 * What falls outside all three is the interesting part: a property the
 * manifest gives **no way to set**. Those are the platform's — a blob
 * key, a content type, a width, the renditions — and they are exactly the
 * payload of the intent Media withholds. So the withholding does a second
 * job it was not designed for: because a withheld intent is absent from
 * the manifest, its members are absent from every form derived from one,
 * and a client asking an author to type a content type becomes unsayable
 * rather than merely discouraged.
 *
 * Computed from the manifest, so a tenth type's create form is right the
 * day it is registered and no list here names a type.
 */
export function creationSchema(manifest: Manifest | undefined, typeName: string): Schema | undefined {
  const fields = fieldsSchema(manifest, typeName)
  if (!fields) return undefined

  const settable = intentSettable(manifest, typeName)
  const fixed = new Set(typeOf(manifest, typeName)?.fixed ?? [])
  const offered: Record<string, Schema> = {}
  const required: string[] = []

  for (const property of orderedProperties(fields, fields)) {
    if (!property.required && !settable.has(property.name) && !fixed.has(property.name)) continue
    // The same two gates every other derived form applies. A property
    // naming part of the tree is a placement, and a placement into a
    // component that does not exist yet is not a thing an author can
    // mean; a property this client cannot draw would render a heading
    // with nothing under it.
    if (namesTheTree(property.schema, fields)) continue
    if (!isDrawable(property.schema, fields)) continue

    offered[property.name] = property.schema
    if (property.required) required.push(property.name)
  }

  if (Object.keys(offered).length === 0) return undefined

  // A condition whose discriminator did not survive the three admissions
  // is a question this form cannot ask, and a property waiting on an
  // answer that never comes is one an author could never reach. Dropping
  // the condition offers it unconditionally, which is what the remaining
  // schema honestly describes — the same call the platform makes when it
  // strips a reserved member out of a payload.
  for (const [name, property] of Object.entries(offered)) {
    const when = property['x-when']
    if (when && !offered[when.field]) {
      offered[name] = { ...property, 'x-when': undefined }
    }
  }

  // `$defs` is deliberately absent: it lives on the root schema, and the
  // caller passes the *fields* schema as the root so references still
  // resolve. A copy here would be a second place for them to live.
  return { type: 'object', properties: offered, required }
}

/**
 * What an **inline placement** asks an author for, or nothing at all
 * where a type may be placed empty and filled in afterwards.
 *
 * An inline child has no stream, so the placement *is* its first
 * revision — the same thing `component.created` is for a standalone one.
 * That is why the question is asked of the fields schema rather than of
 * an intent's payload, and it is why a client that placed `fields: {}`
 * unconditionally was wrong for any type whose document cannot be empty:
 * a Link with no label and a Heading with no rank are refused by the
 * surface, and the author met the refusal rather than the question.
 *
 * **Required is the switch, and a create's form is the answer.** Whether
 * to ask at all is decided by what the type marks required, which is the
 * narrow claim the schema already makes — a property with no `omitempty`
 * is one the type says a document of it always carries. A Container, a
 * Grid, a Menu and a photo gallery mark none, so they are placed on the
 * press exactly as before and no form stands in front of a click.
 *
 * What is asked, once something is, is everything a create would ask,
 * because a valid first revision is more than its required members and
 * only the type knows how much more. A Link marks `kind` and `label`
 * required and is refused without an address as well — external needs an
 * `href` and internal needs a `page`, which is a rule no JSON Schema
 * `required` can state. Offering the create's whole form is what leaves
 * an author able to satisfy it; offering the required half alone would
 * ask two of the three questions and fail on the third.
 */
export function inlineSchema(manifest: Manifest | undefined, typeName: string): Schema | undefined {
  const fields = fieldsSchema(manifest, typeName)
  if (!fields) return undefined

  const cannotBeEmpty = orderedProperties(fields, fields).some(
    (property) =>
      property.required && !namesTheTree(property.schema, fields) && isDrawable(property.schema, fields),
  )
  return cannotBeEmpty ? creationSchema(manifest, typeName) : undefined
}

/**
 * Whether an author has stated everything the placement is refused
 * without.
 *
 * The control enforcing what the control can, and no more: `SchemaForm`
 * holds no rules and the fold is the authority, but a dialog that placed
 * a document it can see is incomplete would spend a round trip to be
 * told so. It is the same gate the pattern setup applies to its two
 * references, for the same reason.
 *
 * Only the *required* members are gated, so the rules a schema cannot
 * state are still the surface's to enforce — an author who names no
 * address for an external link is told so by the platform, which is the
 * one place that rule lives.
 */
export function inlineReady(schema: Schema | undefined, value: Record<string, unknown>): boolean {
  if (!schema) return true
  return (schema.required ?? []).every((name) => {
    const member = value[name]
    return member !== undefined && member !== '' && member !== null
  })
}

/**
 * Every property name some intent this type declares could set.
 *
 * By name, and only by name — the same join `storedValue` makes, and the
 * same limit: a payload member named differently from the field it writes
 * is not recognised, and the property simply is not offered at creation.
 * Guessing at the correspondence would be worse than the omission, since
 * the author can still set it from the editor afterwards.
 */
function intentSettable(manifest: Manifest | undefined, typeName: string): Set<string> {
  const names = new Set<string>()
  for (const intent of typeOf(manifest, typeName)?.intents ?? []) {
    const payload = asSchema(intent.payload)
    if (!payload) continue
    for (const property of orderedProperties(payload, payload)) names.add(property.name)
  }
  return names
}

/**
 * The fields a create actually sends: what the author put in, with the
 * untouched left out.
 *
 * A Go zero value and an absent member decode to the same document, so
 * this changes nothing about what is stored — it changes what the
 * *stream* says. `component.created` is the one event that can never be
 * amended, and one that records `caption: ""` claims the author cleared a
 * caption when they only left a box alone. The distinction costs nothing
 * to keep and cannot be recovered later.
 */
export function creationFields(value: Record<string, unknown>): Record<string, unknown> {
  const stated: Record<string, unknown> = {}
  for (const [name, member] of Object.entries(value)) {
    if (member === undefined || member === '' || member === false) continue
    if (isRecord(member)) {
      const nested = creationFields(member)
      if (Object.keys(nested).length > 0) stated[name] = nested
      continue
    }
    stated[name] = member
  }
  return stated
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
